import { useState } from "react";
import { MICRONUTRIENTS, SOURCE_NAMES } from "./nutrition-model";

const SOURCE_BADGES = { USDA: "USDA", OPEN_FOOD_FACTS: "OFF", FATSECRET: "FatSecret" };

const FACT_NAMES = {
  sugar: "sugars",
  saturatedFat: "saturated fat",
  sodium: "sodium",
  fiber: "fibre",
  transFat: "trans fat",
  addedSugar: "added sugars",
  calories: "energy",
  novaGroup: "processing level",
};
const MICRO_NAMES = Object.fromEntries(MICRONUTRIENTS.map(([code, name]) => [code, name]));
const STEP_NAMES = { BARCODE: "Barcode", USDA: "USDA", OPEN_FOOD_FACTS: "Open Food Facts", FATSECRET: "FatSecret" };
const STEP_ICONS = { MATCHED: "✓", FILLED: "+", POSSIBLE: "?", REJECTED: "✗", NOT_FOUND: "–", ERROR: "!", SKIPPED: "·" };
const fieldName = (f) => {
  const code = f.replace("micronutrients.", "");
  return FACT_NAMES[code] ?? MICRO_NAMES[code] ?? code.replace(/([A-Z])/g, " $1").toLowerCase();
};
const pct = (c) => `${Math.round(c * 100)}%`;

/**
 * "Find nutrition automatically": barcode box, what was filled and from
 * where, and the other matches the admin can pick instead.
 *
 * lookup: { state: 'idle'|'busy'|'done'|'error', filled?: candidate, candidates, warnings, error }
 */
function AutoFillBox({ barcode, onBarcode, lookup, onFind, onUse, onDiscard }) {
  const [showAll, setShowAll] = useState(false);
  const { state, filled, complete, candidates = [], steps = [], warnings = [], error } = lookup;
  const others = candidates.filter((c) => c.recordId !== filled?.recordId);
  const stillMissing = complete ? complete.missing : (filled?.missing ?? []);

  return (
    <div className="nutri-autofill">
      <div className="nutri-row">
        <input
          className="form-control"
          placeholder="Barcode on the pack (optional)"
          inputMode="numeric"
          value={barcode}
          onChange={(e) => onBarcode(e.target.value.replace(/\D/g, ""))}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), onFind())}
        />
        <button type="button" className="btn btn-purple-bg" disabled={state === "busy"} onClick={onFind}>
          {state === "busy" ? "Searching…" : "Find nutrition automatically"}
        </button>
      </div>
      <p className="nutri-muted mb-0">
        Checks the barcode first, then searches by name in order: USDA, Open Food Facts, FatSecret. The first entry
        that is this exact food (same food, same form: juice or whole, raw or cooked, nothing added) gives the
        values; the other databases then fill only what it lacks, and the rest is worked out where it can be (e.g.
        no cholesterol in plants). If no entry is this food, enter the values by hand.
      </p>

      {state === "done" && steps.length > 0 && (
        <ol className="nutri-steps">
          {steps
            .filter((s) => s.outcome !== "SKIPPED")
            .map((s) => (
              <li key={s.source} className={`nutri-step nutri-step-${s.outcome}`}>
                <span className="nutri-step-icon">{STEP_ICONS[s.outcome]}</span> <strong>{STEP_NAMES[s.source]}</strong>:{" "}
                {s.outcome === "MATCHED" ? `matched ${s.detail}` : s.outcome === "FILLED" ? `filled gaps from ${s.detail}` : s.detail}
              </li>
            ))}
        </ol>
      )}

      {state === "busy" && <div className="nutri-status nutri-status-info">Searching nutrition databases…</div>}
      {state === "error" && <div className="nutri-status nutri-status-error">{error}</div>}

      {state === "done" && filled && (
        <div className="nutri-status nutri-status-ok">
          <strong>Filled from {SOURCE_NAMES[filled.source]}:</strong>{" "}
          <a href={filled.url} target="_blank" rel="noreferrer">
            {filled.name}
          </a>
          {filled.brand && ` · ${filled.brand}`}
          {filled.byBarcode ? " · exact barcode match" : ` · ${pct(filled.confidence)} name match`}
          <div>
            {filled.byBarcode
              ? "Check the values against the pack, then save."
              : "Matched by name: make sure it is the same product, then save."}
            {stillMissing.length > 0 && (
              <> Still needed: <strong>{stillMissing.map(fieldName).join(", ")}</strong>.</>
            )}
          </div>
          {complete?.fills.map((f) => (
            <div key={f.candidate.recordId}>
              Gaps filled from {SOURCE_NAMES[f.candidate.source]}{" "}
              <a href={f.candidate.url} target="_blank" rel="noreferrer">
                {f.candidate.name}
              </a>
              : {f.fields.map(fieldName).join(", ")}.
            </div>
          ))}
          {complete && Object.keys(complete.calculated).length > 0 && (
            <div>
              Calculated:{" "}
              {Object.entries(complete.calculated)
                .map(([f, why]) => `${fieldName(f)} (${why})`)
                .join("; ")}
              .
            </div>
          )}
          {onDiscard && (
            <button type="button" className="btn btn-sm btn-white-bg mt-2" onClick={onDiscard}>
              Don't use this — undo
            </button>
          )}
        </div>
      )}

      {state === "done" && !filled && candidates.some((c) => c.identity?.ok !== false) && (
        <div className="nutri-status nutri-status-warn">
          No confirmed match. Check the possible matches below, or enter the values by hand.
        </div>
      )}

      {state === "done" && !filled && !candidates.some((c) => c.identity?.ok !== false) && (
        <div className="nutri-status nutri-status-warn">
          This exact food is not in USDA, Open Food Facts or FatSecret. Enter the values by hand from the pack
          label, a lab report or ICMR-NIN food composition tables.
        </div>
      )}

      {warnings.length > 0 && <p className="nutri-muted mb-0">Note: {warnings.join("; ")}</p>}

      {state === "done" && (filled ? others.length > 0 : candidates.length > 0) && (
        <>
          {filled && (
            <button type="button" className="nutri-link mt-2" onClick={() => setShowAll(!showAll)}>
              {showAll ? "Hide other matches" : `Not the right product? See ${others.length} other matches`}
            </button>
          )}
          {(showAll || !filled) && (
            <ul className="nutri-candidates">
              {(filled ? others : candidates).map((c) => (
                <li key={c.recordId}>
                  <span className={`nutri-source nutri-source-${c.source}`}>
                    {SOURCE_BADGES[c.source] ?? c.source}
                  </span>
                  <span className="nutri-candidate-name">
                    <a href={c.url} target="_blank" rel="noreferrer">
                      {c.name}
                    </a>
                    {c.brand && <span className="nutri-muted"> · {c.brand}</span>}
                    <span className="nutri-muted">
                      {" "}
                      · {pct(c.confidence)} match
                      {c.missing.length > 0 && ` · missing ${c.missing.map((m) => FACT_NAMES[m] ?? m).join(", ")}`}
                    </span>
                    {c.identity?.ok === false && (
                      <span className="nutri-different"> Different food: {c.identity.reasons.join(", ")}</span>
                    )}
                  </span>
                  <button type="button" className="btn btn-sm btn-white-bg" onClick={() => onUse(c)}>
                    Use this
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

export default AutoFillBox;
